'use client'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { mq, presets, rgbAlpha, vw } from '@/styles'
import { Img } from '@/components'
import { Price } from './Price'
import { StockWarning } from './StockWarning'
import { ButtonAddToCart } from '../cart/ButtonAddToCart'
import { E_CART_DELIVERY_TYPE, E_DELIVERY_TYPE, TypeCmsCartLine, type TypeCmsVariant } from '@/schema'
import { Tags } from './Tags'
import { useModal, useProduct, useSession } from '@/hooks'
import { useCart } from '@/context/project/CartContext'
import { DY_GUEST_CART_ALLOWED, LANG } from '@/utils'
import { MODALS } from '@/context'

type Props = {
  data: TypeCmsVariant & { idx?: number }
  narrow?: boolean
  onClick?: () => void
}

type CheckIfVariantPurchasableParams = {
	userLoggedIn: boolean
	deliveryType: TypeCmsVariant['deliveryType']
	stock: TypeCmsVariant['stock']
	minSaleUnits: TypeCmsVariant['minSaleUnits']
}
export const checkIfVariantPurchasable = ({ userLoggedIn, deliveryType, stock, minSaleUnits }: CheckIfVariantPurchasableParams) => {
	// Check user and store status
	if(!userLoggedIn && !DY_GUEST_CART_ALLOWED) return false
	// Check variant related stuff:
	if(deliveryType === E_DELIVERY_TYPE.ON_DEMAND) return true
	// Check stock:
	// PENDING CHECK CURRENT CART TYPE (if is fresh cart, frozen cart cannot be purchased and viceversa)
	return stock >= minSaleUnits
}

export const getCartTypeFromLines = (lines: TypeCmsCartLine[]) => {
  const hasFrozen = lines.some(line => line.variant.deliveryType === E_DELIVERY_TYPE.FROZEN)
  if(hasFrozen) return E_CART_DELIVERY_TYPE.FROZEN
  else return E_CART_DELIVERY_TYPE.FRESH
}

export const VariantCard:FC<Props> = memo(({ data, onClick }) => {
  const {
    bigcommerceId: variantBigcommerceId,
    deliveryType,
    media,
    minSaleUnits,
    name: variantName,
    price,
    product,
    stock,
    sku,
    stockWarning,
  } = data
  const { openProduct } = useProduct()
  const pathname = usePathname()
  const { updatePayload: updateCartTypePayload } = useModal(MODALS.CART_TYPE)
  const frozen = deliveryType === E_DELIVERY_TYPE.FROZEN

  const { user } = useSession()
  const { cartLines } = useCart()
	
  const cartType = useMemo(() => getCartTypeFromLines(cartLines), [cartLines])
  const isVariantPurchasable = useMemo(() => checkIfVariantPurchasable({ userLoggedIn: !!user, deliveryType, stock, minSaleUnits }), [user, deliveryType, stock, minSaleUnits])

  const getProductUrl = () => {
    const basePath = pathname.includes('/catalog/') ? '/catalog' : '/shop'
    return `${basePath}/product/${product?.slug?.[LANG ?? 'en'] || product?.slug}`
  }

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const target = e.target as HTMLElement
    if (target.closest('[data-add-to-cart]')) return
    onClick?.()

    const url = getProductUrl()
    if (pathname?.includes('/product/')) {
      window.history.replaceState(null, '', url)
    } else {
      window.history.pushState(null, '', url)
    }
    const productSlug =
      typeof product?.slug === 'string'
        ? product.slug
        : product?.slug?.[LANG ?? 'en']
    if (productSlug) openProduct(productSlug)
  }

  return (
    <Article
      key={variantBigcommerceId}
      $deliveryType={deliveryType}
      $frozen={frozen}
      data-sku={sku}
    >
      <Link className='link' href={getProductUrl()} rel='bookmark' onClick={handleCardClick}>
        <Img className='media' media={media?.[0]?.medium} />
        <hgroup>
          <h3>{product?.name}</h3>
          <p className='desc'>
            {product?.brand && <span>{product.brand?.name}</span>}
            {variantName && <span>{variantName}</span>}
          </p>
          {((user || DY_GUEST_CART_ALLOWED) && deliveryType !== E_DELIVERY_TYPE.ON_DEMAND) && (
            <StockWarning data={{ stock, minSaleUnits, stockWarning, frozen }} />
          )}
          {price && <Price data={price} />}
        </hgroup>
        <Tags labels={product?.labels} filters={product?.filters} frozen={frozen} discount={price?.discount} />
      </Link>
      {isVariantPurchasable && (
        <>
          {cartLines?.length > 0 && ((cartType !== E_CART_DELIVERY_TYPE.FROZEN && deliveryType === E_DELIVERY_TYPE.FROZEN) || (cartType === E_CART_DELIVERY_TYPE.FROZEN && deliveryType !== E_DELIVERY_TYPE.FROZEN)) ? <ButtonTriggerCartType onClick={() => { updateCartTypePayload({ open: true, payload: { data: { cartType }}}) }} data-cart-type-trigger $frozen={cartType === E_CART_DELIVERY_TYPE.FROZEN}>i</ButtonTriggerCartType> :
          <ButtonAddToCart frozen={frozen} variant={data} data-add-to-cart />}
        </>
      )}
			{product?.brand && <a className='srt-only' href={`/catalog/brand/${product.brand?.slug[LANG]}`}>{product.brand?.name}</a>}
    </Article>
  )
})


export const ButtonTriggerCartType = styled.button<{ $frozen: boolean }>`
  border-radius: 100px;
  background-color: #cdcdcd;
  /* background-color: ${({ $frozen }) => !$frozen ? rgbAlpha('--color-card-border-hover', 0.6) : 'var(--color-card-border-hover--frozen)'}; */
  color: var(--color-white);
  height: 32px;
  font-family: var(--font-basier-circle) !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  margin: 8px;
  width: 32px;
`

const Article = styled.article<{
  $frozen: boolean
  $deliveryType: TypeCmsVariant['deliveryType']
}>`
  background-color: ${({ $frozen }) =>
    !$frozen
      ? 'var(--color-card-background)'
      : 'var(--color-card-background--frozen)'};
  border-radius: 20px;
  border: 2px solid var(--color-background);
  color: ${({ $frozen }) => !$frozen ? 'var(--color-text)' : 'var(--color-card-text--darker--frozen)'};
  display: grid;
  grid-template-columns: 2fr;
  display: flex;
  flex-direction: column;
  font-family: var(--font-graphik);
  height: ${vw(280, 'mobile')};
  max-height: 330px;
  min-height: 280px;
  overflow: hidden;
  position: relative;
  transition: 200ms border-color linear;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(280, 'tablet')};
    min-height: 280px;
  }

  ${mq.greaterThan('tablet')} {
    height: ${vw(330, 'tablet')};
  }

  ${mq.greaterThan('desktop')} {
    height: 330px;
    max-width: 250px;
    min-width: 190px;
    width: 100%;
  }

  &:hover:not(.selected) {
    border-color: ${({ $frozen }) =>
      !$frozen
        ? rgbAlpha('--color-card-border-hover', 0.6)
        : 'var(--color-card-border-hover--frozen)'};
  }

  &.selected {
    border-color: ${({ $frozen }) =>
      !$frozen
        ? 'var(--color-card-border--selected)'
        : 'var(--color-card-border--selected--frozen)'};
  }

  .link {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: flex-start;
    max-height: 100%;
    width: 100%;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
  }

  .media {
    background-color: var(--color-white);
    border-radius: 12px;
    height: ${vw(150, 'mobile')};
    margin: 8px;
    min-height: 150px;
    mix-blend-mode: multiply;
    overflow: hidden;
    width: calc(100% - 16px);

    ${mq.greaterThan('nexus7')} {
      height: ${vw(150, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(200, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 200px;
    }
  }

  hgroup {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: .5px;
    height: 100%;
    padding: 0 8px 12px;
    width: 100%;

    .desc {
      ${presets.font15_1()}
    }
  }

  h3,
  p {
    text-align: left;
  }

  h3,
  p span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

  h3 {
    color: ${({ $frozen }) =>
      !$frozen
        ? 'var(--color-text--darker)'
        : 'var(--color-card-text--darker--frozen)'};
    ${presets.font15()}
  }

  p {
    align-items: flex-start;
    color: ${({ $frozen }) =>
      !$frozen ? 'var(--color-text)' : 'var(--color-card-text--frozen)'};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .price {
    justify-self: flex-end;
    margin-top: auto;

    /* span {
      &:first-child {
        &:last-child {
          margin-top: 14px;
        }
      }
    } */
  }

  > div,
  button[data-cart-type-trigger],
  button[data-add-to-cart] {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 2;
  }
`
